'use client';

import { useState, useEffect } from 'react';
import { userPreferencesService, UserPreferences } from '@/lib/services/userPreferencesService';

interface PreferencesEditorProps {
  profileId: string;
}

export default function PreferencesEditor({ profileId }: PreferencesEditorProps) {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'services' | 'notifications'>('services');

  useEffect(() => {
    loadPreferences();
  }, [profileId]);

  const loadPreferences = async () => {
    try {
      const data = await userPreferencesService.getPreferencesByProfileId(profileId);
      if (data) {
        setPreferences(data);
      } else {
        setPreferences({
          userProfileId: profileId,
          preferredServiceTypes: [],
          preferredTimeSlots: [],
          emailNotifications: true,
          smsNotifications: true,
          pushNotifications: true,
          notifyBookingConfirmation: true,
          notifyDriverAssigned: true,
          notifyStatusUpdates: true,
          notifyMessages: true,
          notifyPromotions: false,
          notifyReminders: true,
        });
      }
    } catch (error: any) {
      console.error('Failed to load preferences:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!preferences) return;

    setSaving(true);
    try {
      if (preferences.id) {
        await userPreferencesService.updatePreferences(profileId, preferences);
      } else {
        await userPreferencesService.createPreferences(preferences);
      }

      alert('Preferences saved successfully!');
      loadPreferences();
    } catch (error: any) {
      alert(`Failed to save preferences: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleServiceType = (serviceType: string) => {
    if (!preferences) return;
    const current = preferences.preferredServiceTypes || [];
    const updated = current.includes(serviceType)
      ? current.filter(s => s !== serviceType)
      : [...current, serviceType];
    setPreferences({ ...preferences, preferredServiceTypes: updated });
  };

  const toggleTimeSlot = (timeSlot: string) => {
    if (!preferences) return;
    const current = preferences.preferredTimeSlots || [];
    const updated = current.includes(timeSlot)
      ? current.filter(t => t !== timeSlot)
      : [...current, timeSlot];
    setPreferences({ ...preferences, preferredTimeSlots: updated });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!preferences) return null;

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-2xl shadow-card p-6">
        <div className="flex space-x-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('services')}
            className={`pb-4 px-4 font-medium transition-colors ${
              activeTab === 'services' ?'border-b-2 border-brand-purple text-brand-purple' :'text-gray-500 hover:text-gray-700'
            }`}
          >
            Service Preferences
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`pb-4 px-4 font-medium transition-colors ${
              activeTab === 'notifications'
                ? 'border-b-2 border-brand-purple text-brand-purple' :'text-gray-500 hover:text-gray-700'
            }`}
          >
            Notifications
          </button>
        </div>
      </div>

      {/* Service Preferences Tab */}
      {activeTab === 'services' && (
        <div className="bg-white rounded-2xl shadow-card p-6 space-y-6">
          <div>
            <h3 className="text-lg font-bold text-dark-navy mb-4">Preferred Service Types</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { value: 'furniture_delivery', label: 'Furniture Delivery' },
                { value: 'appliance_moving', label: 'Appliance Moving' },
                { value: 'marketplace_pickup', label: 'Marketplace Pickup' },
                { value: 'office_moving', label: 'Office Moving' },
                { value: 'storage_delivery', label: 'Storage Delivery' },
                { value: 'other', label: 'Other' },
              ].map((service) => (
                <button
                  key={service.value}
                  onClick={() => toggleServiceType(service.value)}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    preferences.preferredServiceTypes?.includes(service.value)
                      ? 'border-brand-purple bg-brand-purple/10 text-brand-purple' :'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {service.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold text-dark-navy mb-4">Preferred Time Slots</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { value: 'morning', label: 'Morning (8AM-12PM)' },
                { value: 'afternoon', label: 'Afternoon (12PM-5PM)' },
                { value: 'evening', label: 'Evening (5PM-8PM)' },
                { value: 'weekend', label: 'Weekend' },
              ].map((slot) => (
                <button
                  key={slot.value}
                  onClick={() => toggleTimeSlot(slot.value)}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    preferences.preferredTimeSlots?.includes(slot.value)
                      ? 'border-brand-purple bg-brand-purple/10 text-brand-purple' :'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {slot.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-navy mb-2">
              Maximum Budget Per Booking
            </label>
            <input
              type="number"
              value={preferences.maxBudgetPerBooking || ''}
              onChange={(e) => setPreferences({ ...preferences, maxBudgetPerBooking: parseFloat(e.target.value) })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
              placeholder="Enter maximum budget"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-navy mb-2">
              Accessibility Requirements
            </label>
            <textarea
              value={preferences.accessibilityRequirements || ''}
              onChange={(e) => setPreferences({ ...preferences, accessibilityRequirements: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
              placeholder="Describe any accessibility requirements"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-navy mb-2">
              Special Handling Preferences
            </label>
            <textarea
              value={preferences.specialHandlingPreferences || ''}
              onChange={(e) => setPreferences({ ...preferences, specialHandlingPreferences: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
              placeholder="Describe any special handling needs"
            />
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="bg-white rounded-2xl shadow-card p-6 space-y-6">
          <div>
            <h3 className="text-lg font-bold text-dark-navy mb-4">Notification Channels</h3>
            <div className="space-y-3">
              {[
                { key: 'emailNotifications', label: 'Email Notifications' },
                { key: 'smsNotifications', label: 'SMS Notifications' },
                { key: 'pushNotifications', label: 'Push Notifications' },
              ].map((channel) => (
                <label key={channel.key} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(preferences as any)[channel.key]}
                    onChange={(e) => setPreferences({ ...preferences, [channel.key]: e.target.checked })}
                    className="w-5 h-5 text-brand-purple rounded focus:ring-brand-purple"
                  />
                  <span className="text-dark-navy">{channel.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-bold text-dark-navy mb-4">Notification Types</h3>
            <div className="space-y-3">
              {[
                { key: 'notifyBookingConfirmation', label: 'Booking Confirmations' },
                { key: 'notifyDriverAssigned', label: 'Driver Assignments' },
                { key: 'notifyStatusUpdates', label: 'Status Updates' },
                { key: 'notifyMessages', label: 'New Messages' },
                { key: 'notifyReminders', label: 'Booking Reminders' },
                { key: 'notifyPromotions', label: 'Promotions & Offers' },
              ].map((notif) => (
                <label key={notif.key} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(preferences as any)[notif.key]}
                    onChange={(e) => setPreferences({ ...preferences, [notif.key]: e.target.checked })}
                    className="w-5 h-5 text-brand-purple rounded focus:ring-brand-purple"
                  />
                  <span className="text-dark-navy">{notif.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-brand-purple text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}